import React, {useCallback, useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useParams} from "react-router";
import {makeStyles} from '@material-ui/core/styles';
import TreeView from '@material-ui/lab/TreeView';
import {set} from "object-path-immutable";
import {fetchTreeThunk} from "../../reducers/fetchTree.thunk";
import {insertSchemaProperties, setApplicationId, setSelectedNodes} from "../../reducers/applicationBuilder.slice";
import request from "../../../../Services/API/Requests";
import findByString from "../../../../Utils/Form/FormDynamic/Utils/getValueByString";
import {multiLanguageValue} from "../../../JsonScreen/Utils/lib";
import filterDataByString
    from "../../../JsonScreen/Screen/ScreenComponents/ListScreenComponent/List/Utils/filterDataByString";
import {CloseSquare, MinusSquare, PlusSquare} from "./Tree/TreeUtils";
import StyledTreeItem from "./Tree/TreeItem";
import RenderTree from "./Tree/RenderTree";
import ContextMenu from "./Tree/ContextMenu";
import {SourceTypes} from "../Designers/ScreenDesigner/ToolBar/ToolBarUtils/ItemTypes";

const useStyles = makeStyles(() => ({
    root: {
        height: '100%',
        flexGrow: 1,
        maxWidth: 350,
        width: '100%'
    },
    container: {
        padding: 10,
        width: '100%'
    }
}))

const initialContextState = {
    mouseX: null,
    mouseY: null,
    type: null
};
const ApplicationTree = () => {
    const params = useParams();
    const classes = useStyles();
    const [data, setData] = React.useState(null);
    const [anchorEl, setAnchorEl] = useState(initialContextState);
    const [expanded, setExpanded] = React.useState([]);
    const appTree = useSelector(state => state.applicationBuilder.appTree)
    const filter = useSelector(state => state.applicationBuilder.filter)
    const selectedNodes = useSelector(state => state.applicationBuilder.selectedNodes)
    const dispatch = useDispatch();
    const userLanguage = useSelector(state => state.app.userLanguage);

    const handleToggle = async (event, nodeIds) => {
        const currentNode = nodeIds[0].split('.')
        if (nodeIds.length > expanded.length && currentNode.length === 4) {
            const newPath = nodeIds[0].split(".").slice(2, nodeIds[0].split(".").length).join(".")
            const item = findByString(appTree, "0." + newPath)

            let res = await request({
                url: '/schema/read',
                data: {id: item.id},
            })

            const properties = createSchemaProperties(res.properties, item.id, item.id);

            dispatch(insertSchemaProperties({
                path: nodeIds[0],
                properties
            }))
        }
        if (event.metaKey || event.ctrlKey) {
            return
        }
        setExpanded(nodeIds);
    };

    const handleSelect = (event, nodeIds) => {
        event.preventDefault();
        event.stopPropagation();
        if (event.metaKey || event.ctrlKey) {
            dispatch(setSelectedNodes({nodeIds, multiple: true}));
            return
        }
        dispatch(setSelectedNodes({nodeIds, multiple: false}));
    };

    //init data
    useEffect(() => {
        dispatch(fetchTreeThunk(params.id))
        dispatch(setApplicationId(params.id))
    }, []);

    //filter data
    useEffect(() => {
        const columns = ['id', 'name'];
        let newData = appTree;

        if (Object.keys(filter).length) {
            Object.keys(filter).forEach(key => {
                const index = appTree.findIndex(v => v.id === key);
                newData = set(newData, `${index}.children`, filterDataByString(appTree[index].children, columns, filter[key]))
            })
        }

        setData(newData)
    }, [JSON.stringify(appTree), JSON.stringify(filter)]);

    const openContext = useCallback((e, type, recordId) => {
        e.preventDefault();
        e.stopPropagation();
        setAnchorEl({
            mouseX: e.clientX - 2,
            mouseY: e.clientY - 4,
            type,
            recordId
        })
    }, [])

    return (
        <div className={classes.container}>
            <TreeView
                className={classes.root}
                defaultExpanded={['1']}
                defaultCollapseIcon={<MinusSquare/>}
                defaultExpandIcon={<PlusSquare/>}
                defaultEndIcon={<CloseSquare/>}
                expanded={expanded}
                selected={selectedNodes}
                onNodeToggle={handleToggle}
                onNodeSelect={handleSelect}
            >
                <StyledTreeItem nodeId="1" label={params.id}>
                    <RenderTree items={data}
                                openContext={openContext}
                    />
                </StyledTreeItem>
            </TreeView>
            <ContextMenu
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(initialContextState)}
            />
        </div>
    )
}

export default ApplicationTree;


function createSchemaProperties(schema = [], parentPath, entity) {
    return schema.map((p) => {
        if (p.type === 'object' || p.type === 'entityEmbedded' || p.type === 'entityMixed') {
            const schema = p.type === 'object' ? p.properties : p.schema?.properties || [];
            //create screen components
            p.source = createGroup({
                field: {
                    ...p,
                    schema: {
                        properties: schema
                    }
                },
                path: `${parentPath}`
            });

            //create properties for tree render
            p.properties = createSchemaProperties(schema, parentPath ? `${parentPath}.${p.name}` : p.name);
        }

        let path = parentPath ? `${parentPath}.${p.name}` : p.name;
        if (!p.source) {
            p.source = {
                type: 'field',
                properties: {
                    ...p,
                    path,
                },
                styles: [
                    {width: '300px', margin: '', fullWidth: false},
                    {width: '100px', margin: ''}
                ]
            }
        }

        return {
            id: p.name,
            name: `${multiLanguageValue(p.label, 'en')} (${p.name}, ${p.type}, required: ${Boolean(p.required)})`,
            source: p.source,
            properties: p.properties
        }
    })
}

function createGroup({field, source = [], level = 1, path, arrayNestedPath}) {
    const {label, name, schema: {properties = []} = {}, isArray} = field;
    const arrayType = field.arrayType || 'vertical';
    let arrayContainer = {
        vertical: {
            type: "forEach",
            parent: name,
            properties: {
                path: `${path}.${name}`,
                creatable: true,
                removable: true,
                editScreen: ''
            },
            styles: [{width: '100%'}],
            children: []
        },
        horizontal: {
            type: "fieldArray",
            parent: name,
            properties: {
                path: `${path}.${name}`,
                creatable: true,
                removable: true,
                arrayType: 'horizontal',
            },
            styles: [{width: '100%'}],
            children: []
        }
    };
    let labelComponent = {
        type: 'label',
        properties: {label: label},
        styles: [{padding: `0 0 0 ${level * 10}px`}]
    };
    let entityMixedField = {
        field: {
            type: 'field',
            styles: [
                {width: '300px', margin: '', fullWidth: false},
                {width: '100px', margin: `0 0 0 ${(level + 1) * 10}px`}
            ],
            properties: {
                type: 'entityMixed',
                name: field.name,
                label: field.label,
                path: `${path}.${name}`,
                ...field
            }
        },
        container: {
            vertical: {
                type: 'forEach',
                parent: name,
                styles: [{width: '100%'}],
                properties: {
                    path: `${path}.${name}`,
                    creatable: true,
                    removable: true,
                    editScreen: ''
                },
                children: []
            },
            horizontal: {
                type: "fieldArray",
                name: field.name,
                properties: {
                    path: `${path}.${name}`,
                    arrayType: field.arrayType || 'vertical',
                    hidden: field.hidden
                },
                styles: [{width: '100%'}],
                children: []
            }

        }
    };

    if (field.type === 'entityMixed') {
        source.push(entityMixedField.field)
    } else {
        source.push(labelComponent)
    }

    if (isArray) {
        if (field.type === 'entityMixed') {
            source.push(entityMixedField.container[arrayType])
        } else {
            source.push(arrayContainer[arrayType]) //change to forEach component
        }
    }

    properties.forEach(item => {
        if (item.type === 'entityEmbedded' || item.type === 'object' || item.type === 'entityMixed') {
            createGroup({
                source: isArray ? arrayContainer[arrayType].children : source,
                field: {
                    ...item,
                    schema: item.type === 'entityEmbedded' ? item.schema : {properties: item.properties}
                },
                level: level + 1,
                path: isArray ? arrayNestedPath ? `${arrayNestedPath}.${item.name}` : `${path}.${item.name}` :
                    field.type === 'entityMixed' ? `${name}.${path}` : path,
                arrayNestedPath: isArray ? arrayNestedPath ? `${arrayNestedPath}.${item.name}` : item.name : null
            });
            return
        }

        if (isArray) {
            const children = {
                type: SourceTypes.field,
                properties: {...item, path: arrayNestedPath ? `${arrayNestedPath}.${name}` : item.name},
                styles: [
                    {
                        width: arrayType === 'horizontal' ? `${100 / properties.length}%` : '300px',
                        margin: '', fullWidth: false
                    },
                    {width: '100px', margin: `0 0 0 ${(level + 1) * 10}px`}
                ]
            };
            if (field.type === 'entityMixed') {
                entityMixedField.container[arrayType].children.push(children);
                return
            } else {
                arrayContainer[arrayType].children.push(children);
            }
            return
        }

        source.push({
            type: SourceTypes.field,
            properties: {
                ...item,
                path: arrayNestedPath ? `${arrayNestedPath}.${item.name}` : `${path}.${name}.${item.name}`
            },
            styles: [
                {
                    width: arrayType === 'horizontal' ? `${100 / properties.length}%` : '300px',
                    fullWidth: false
                },
                {
                    width: '100px',
                    margin: `0 0 0 ${(level + 1) * 10}px`
                }
            ]
        })
    });
    return source
}